/**
 * @file components/audit-tasks/field/CheckInOutSection.tsx
 * @description 到達/離開打卡區塊（內聯版，從老 field.tsx 搬遷）。
 *
 * - 取 GPS 座標（瀏覽器 Geolocation API；失敗時跳過 GPS 仍可打卡）
 * - 線上：直接 POST /api/audit-tasks/:id/check-in /check-out
 * - 離線：native IndexedDB 推 PWA outbox
 * - 成功後呼叫 onChanged 觸發父元件 refetch
 */

import { Button, Space, Typography } from 'antd';
import { message } from '@/lib/antd-app';
import { LogIn, LogOut } from 'lucide-react';
import { useState } from 'react';
import { postWithOutbox } from '@/lib/api-with-outbox';
import { pushOutboxReliable } from '@/pwa/db';
import { useFieldWriteAccess } from '@/hooks/useFieldWriteAccess';

interface Props {
  /** 稽核任務 ID */
  taskId: number;
  /** 已到達時間（ISO；null 表示未到達） */
  arrivedAt: string | null;
  /** 已離開時間（ISO；null 表示未離開） */
  leftAt: string | null;
  /** 打卡狀態變動時呼叫，父元件據此 invalidate task detail */
  onChanged: () => void;
}

/** 取得 GPS 座標。瀏覽器不支援或失敗時 reject。 */
function getGPS(): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('瀏覽器不支援 GPS'));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(new Error(`GPS 定位失敗：${err.message}`)),
      { timeout: 10000 },
    );
  });
}

export function CheckInOutSection({ taskId, arrivedAt, leftAt, onChanged }: Props) {
  const [pendingAction, setPendingAction] = useState<'check-in' | 'check-out' | null>(null);
  // 序61：管理組（manager）唯讀——無打卡能力時隱藏操作按鈕，只保留打卡狀態展示
  const { canCheckIn } = useFieldWriteAccess();

  // ─── 到達打卡 ───────────────────────────────────────────────────
  const checkIn = async () => {
    if (pendingAction) return;
    setPendingAction('check-in');
    try {
      // 浏览器 geolocation 用本机 GPS/cell/wifi sensor，离线也能工作；
      // 此前用 navigator.onLine 门控会在远端农场离线场景下静默丢失位置证明 (PR #2 review #7)
      const pos = await getGPS().catch(() => null);
      if (!pos) message.warning('無法獲取 GPS，將不記錄位置');
      const gpsLat = pos?.lat;
      const gpsLng = pos?.lng;
      const body = { arrivedAt: new Date().toISOString(), gpsLat, gpsLng };
      const url = `/api/audit-tasks/${taskId}/check-in`;
      const outboxMeta = {
        entityType: 'audit-task',
        entityClientLocalId: String(taskId),
      };
      const result = !navigator.onLine
        ? await pushOutboxReliable({ method: 'POST', url, body, ...outboxMeta }).then(() => ({
            offline: true,
          }))
        : await postWithOutbox(url, body, outboxMeta);
      if ((result as { offline?: boolean })?.offline) {
        message.success('到達打卡已加入離線佇列，連線後自動同步');
      } else {
        message.success('到達打卡成功');
      }
      onChanged();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '到達打卡失敗');
    } finally {
      setPendingAction(null);
    }
  };

  // ─── 離開打卡 ───────────────────────────────────────────────────
  const checkOut = async () => {
    if (pendingAction) return;
    setPendingAction('check-out');
    try {
      const body = { leftAt: new Date().toISOString() };
      const url = `/api/audit-tasks/${taskId}/check-out`;
      const outboxMeta = {
        entityType: 'audit-task',
        entityClientLocalId: String(taskId),
      };
      const result = !navigator.onLine
        ? await pushOutboxReliable({ method: 'POST', url, body, ...outboxMeta }).then(() => ({
            offline: true,
          }))
        : await postWithOutbox(url, body, outboxMeta);
      if ((result as { offline?: boolean })?.offline) {
        message.success('離開打卡已加入離線佇列');
      } else {
        message.success('離開打卡成功');
      }
      onChanged();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '離開打卡失敗');
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        到達：
        {arrivedAt ? new Date(arrivedAt).toLocaleString('zh-TW') : '—'}
        {' · '}
        離開：
        {leftAt ? new Date(leftAt).toLocaleString('zh-TW') : '—'}
      </Typography.Text>
      {/* 序61：無打卡能力（如管理組）僅查看狀態，不顯示操作按鈕 */}
      {canCheckIn ? (
        <Space>
          <Button
            type="primary"
            icon={<LogIn size={14} />}
            loading={pendingAction === 'check-in'}
            onClick={() => void checkIn()}
            disabled={!!arrivedAt || pendingAction !== null}
          >
            到達打卡
          </Button>
          <Button
            danger
            icon={<LogOut size={14} />}
            loading={pendingAction === 'check-out'}
            onClick={() => void checkOut()}
            disabled={!arrivedAt || !!leftAt || pendingAction !== null}
          >
            離開打卡
          </Button>
        </Space>
      ) : (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          僅供查看，您沒有現場打卡的操作權限
        </Typography.Text>
      )}
    </Space>
  );
}
